import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import React from 'react';

import Placeholder from '@/config/placeholder.config';
import Cigar from '@/models/Cigar';

export interface SuggestedCigar {
  cigar_id: number;
  cigar_name: string;
  name?: string;
  image_url?: string | null;
  vitola_id?: number | null;
  vitola_name?: string | null;
  score?: number | null;
  match_method?: string | null;
  evidence?: {
    brand_match?: boolean;
    title_similarity?: number | null;
    vitola_match?: boolean;
    dimension_match?: boolean;
  } | null;
}

interface ProductCigar {
  cigar_id?: number;
  id?: number;
  full_name?: string;
  name?: string;
  image_url?: string | null;
  band_image_url?: string | null;
  images?: Array<{
    image_type?: number;
    approved?: boolean;
    image_url?: string | null;
    url?: string | null;
  }>;
}

export interface ProductCigarRow {
  id: number;
  hash_id?: string;
  product_type?: string | null;
  packaging?: { type?: string | null } | null;
  categories?: Array<string | { name?: string; title?: string }> | null;
  contents?: Array<{ cigar?: ProductCigar | null }> | null;
  suggested_cigars?: SuggestedCigar[] | null;
}

interface ProductCigarCellProps {
  product: ProductCigarRow;
  onApproveSuggestion: (productId: number, suggestion: SuggestedCigar) => void;
  onRejectSuggestion: (productId: number, suggestion: SuggestedCigar) => void;
}

interface ProductCigarSuggestionsProps {
  product: ProductCigarRow;
  onApproveSuggestion: ProductCigarCellProps['onApproveSuggestion'];
  onRejectSuggestion: ProductCigarCellProps['onRejectSuggestion'];
}

const NON_CIGAR_TYPES = new Set([
  'accessory',
  'accessories',
  'ashtray',
  'ashtrays',
  'cutter',
  'cutters',
  'gift card',
  'gift cards',
  'gift certificate',
  'gift certificates',
  'humidor',
  'humidors',
  'lighter',
  'lighters',
]);

const subduedTextStyle: React.CSSProperties = {
  color: '#6c757d',
  whiteSpace: 'nowrap',
};

const chipStyle: React.CSSProperties = {
  maxWidth: 240,
  backgroundColor: '#e9ecef',
  color: '#343a40',
};

const suggestionChipStyle: React.CSSProperties = {
  ...chipStyle,
  backgroundColor: '#fff3cd',
  border: '1px solid #d39e00',
  color: '#664d03',
};

const getCategoryNames = (product: ProductCigarRow) =>
  (product.categories || [])
    .map((category) =>
      typeof category === 'string'
        ? category
        : category.name || category.title || ''
    )
    .filter(Boolean);

export const isNonCigarProduct = (product: ProductCigarRow) => {
  const classifications = [
    product.product_type,
    product.packaging?.type,
    ...getCategoryNames(product),
  ];
  return classifications.some((value) =>
    NON_CIGAR_TYPES.has(
      String(value || '')
        .trim()
        .toLowerCase()
    )
  );
};

// Rank by the API's overall score and its title-similarity evidence. Preserve
// the individual values as tie-breakers so the ordering remains deterministic.
export const getSuggestedCigars = (product: ProductCigarRow) =>
  [...(product.suggested_cigars ?? [])].sort((left, right) => {
    const leftScore = Number(left.score || 0);
    const rightScore = Number(right.score || 0);
    const leftTitleSimilarity = Number(left.evidence?.title_similarity || 0);
    const rightTitleSimilarity = Number(right.evidence?.title_similarity || 0);

    return (
      rightScore + rightTitleSimilarity - (leftScore + leftTitleSimilarity) ||
      rightScore - leftScore ||
      rightTitleSimilarity - leftTitleSimilarity
    );
  });

const getCigarImage = (
  cigar: ProductCigar | SuggestedCigar,
  suggested: boolean
) =>
  suggested
    ? cigar.image_url || Placeholder.band
    : Cigar.getBandImage(cigar) || Placeholder.band;

const getCigarName = (cigar: ProductCigar | SuggestedCigar) =>
  ('full_name' in cigar && cigar.full_name) ||
  ('cigar_name' in cigar && cigar.cigar_name) ||
  cigar.name ||
  'Unnamed cigar';

const CigarChip = ({
  cigar,
  suggested = false,
}: {
  cigar: ProductCigar | SuggestedCigar;
  suggested?: boolean;
}) => {
  const name = getCigarName(cigar);
  const imageUrl = getCigarImage(cigar, suggested);
  return (
    <Chip
      size="small"
      title={name}
      label={name}
      avatar={
        <Avatar>
          <img
            alt={`${name} band`}
            src={imageUrl}
            style={{ height: '100%', width: '100%', objectFit: 'contain' }}
            onError={(event) => {
              if (event.currentTarget.src !== Placeholder.band) {
                event.currentTarget.src = Placeholder.band;
              } else {
                event.currentTarget.style.display = 'none';
              }
            }}
          />
        </Avatar>
      }
      style={suggested ? suggestionChipStyle : chipStyle}
      sx={{
        '& .MuiChip-label': {
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiChip-avatar': { objectFit: 'contain' },
      }}
    />
  );
};

const SuggestionActions = ({
  productId,
  suggestion,
  onApproveSuggestion,
  onRejectSuggestion,
}: {
  productId: number;
  suggestion: SuggestedCigar;
  onApproveSuggestion: ProductCigarCellProps['onApproveSuggestion'];
  onRejectSuggestion: ProductCigarCellProps['onRejectSuggestion'];
}) => (
  <span style={{ display: 'inline-flex', marginLeft: 2 }}>
    <IconButton
      size="small"
      title="Approve suggested cigar"
      aria-label="Approve suggested cigar"
      onClick={(event) => {
        event.stopPropagation();
        onApproveSuggestion(productId, suggestion);
      }}
    >
      <CheckIcon fontSize="small" />
    </IconButton>
    <IconButton
      size="small"
      title="Reject suggested cigar"
      aria-label="Reject suggested cigar"
      onClick={(event) => {
        event.stopPropagation();
        onRejectSuggestion(productId, suggestion);
      }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </span>
);

const getLinkedCigars = (product: ProductCigarRow) => {
  const linkedById = new Map<string, ProductCigar>();
  (product.contents || []).forEach((content) => {
    if (!content.cigar) return;
    const { cigar } = content;
    const key = String(cigar.cigar_id || cigar.id || getCigarName(cigar));
    linkedById.set(key, cigar);
  });
  return Array.from(linkedById.values());
};

export const ProductCigarCell = ({
  product,
  onApproveSuggestion,
  onRejectSuggestion,
}: ProductCigarCellProps) => {
  const linkedCigars = getLinkedCigars(product);
  const suggestedCigars = getSuggestedCigars(product);

  if (linkedCigars.length > 1)
    return <span style={subduedTextStyle}>Various</span>;
  if (linkedCigars.length === 1) return <CigarChip cigar={linkedCigars[0]} />;
  if (isNonCigarProduct(product))
    return <span style={subduedTextStyle}>N/A</span>;

  if (suggestedCigars.length > 0) {
    const [topSuggestion] = suggestedCigars;
    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          maxWidth: 310,
          whiteSpace: 'nowrap',
        }}
      >
        <CigarChip cigar={topSuggestion} suggested />
        {suggestedCigars.length > 1 && (
          <span style={{ color: '#856404', marginLeft: 6 }}>
            (+{suggestedCigars.length - 1})
          </span>
        )}
        <SuggestionActions
          productId={product.id}
          suggestion={topSuggestion}
          onApproveSuggestion={onApproveSuggestion}
          onRejectSuggestion={onRejectSuggestion}
        />
      </div>
    );
  }

  return <span style={subduedTextStyle}>Unlinked</span>;
};

export const ProductCigarSuggestions = ({
  product,
  onApproveSuggestion,
  onRejectSuggestion,
}: ProductCigarSuggestionsProps) => {
  const suggestedCigars = getSuggestedCigars(product);
  if (suggestedCigars.length === 0) return null;

  return (
    <div
      className="alert alert-warning suggestions"
      style={{ marginBottom: 16 }}
    >
      <strong>Cigar suggestions</strong>
      <div style={{ marginTop: 8 }}>
        {suggestedCigars.map((suggestion) => (
          <div
            key={`${suggestion.cigar_id}-${suggestion.vitola_id || ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 0',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <CigarChip cigar={suggestion} suggested />
              {suggestion.vitola_name && (
                <small style={{ marginLeft: 8 }}>
                  {suggestion.vitola_name}
                </small>
              )}
            </div>
            <SuggestionActions
              productId={product.id}
              suggestion={suggestion}
              onApproveSuggestion={onApproveSuggestion}
              onRejectSuggestion={onRejectSuggestion}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
